import React, { FC, useCallback, useContext, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ApolloQueryResult } from 'apollo-client';
import { Typography } from '@material-ui/core';

import {
  MySubscriptionB2BLearnerChargebeeInfo,
  MySubscriptionB2CLearnerChargebeeInfo,
  MySubscriptionCompanyOwnerChargebeeInfo,
  MySubscriptionTeamManagerChargebeeInfo,
} from '../../../../types';
import { CardSection } from '../CardSection';
import { translate, updateUserComPlanInfo } from '../../../../utils/helpers';
import CalendarIcon from '../../../../assets/icons/CalendarIcon';
import { colorsPalette } from '../../../../theme';
import RenewIcon from '../../../../assets/icons/RenewIcon';
import { CustomButton } from '../../../../components/UI/CustomButton';
import { IconButton } from '../../../../components/UI/IconButton';
import AddUserIcon from '../../../../assets/icons/AddUserIcon';
import { useAuth, useChargebeeScriptScriptLoader } from '../../../../utils/hooks';
import { Query } from '../../../../types';
import useStyles from './styles';
import { MY_ACCOUNT_LINKS } from '../../pages/const';
import { ScriptsContext } from '../../../../context/ScriptsContext';

declare global {
  interface Window {
    Chargebee: any;
  }
}

interface SubscriptionInfoProps {
  chargeBeeSite?: string;
  info?:
    | MySubscriptionCompanyOwnerChargebeeInfo
    | MySubscriptionTeamManagerChargebeeInfo
    | MySubscriptionB2BLearnerChargebeeInfo
    | MySubscriptionB2CLearnerChargebeeInfo
    | null;
  companyName?: string;
  refetchOwnerSubscription?: (
    variables?: Record<string, any> | undefined,
  ) => Promise<ApolloQueryResult<Pick<Query, 'getMySubscriptionCompanyOwnerChargebeeInfo'>>>;
  setRefetchLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SubscriptionInfo: FC<SubscriptionInfoProps> = ({
  chargeBeeSite,
  companyName,
  info,
  refetchOwnerSubscription,
  setRefetchLoading,
}) => {
  const classes = useStyles();

  useChargebeeScriptScriptLoader();
  const { isChargebeeScriptLoaded } = useContext(ScriptsContext);

  const cbInstance = useMemo(() => {
    if (!isChargebeeScriptLoaded || !chargeBeeSite) return;
    return window?.Chargebee?.init({
      site: chargeBeeSite,
    });
  }, [chargeBeeSite, isChargebeeScriptLoaded]);

  const ownerInfo = info?.__typename === 'MySubscriptionCompanyOwnerChargebeeInfo' ? info : null;
  const b2cInfo = info?.__typename === 'MySubscriptionB2CLearnerChargebeeInfo' ? info : null;

  const allPlacesOccupied = ownerInfo?.seats?.occupied_seats === ownerInfo?.seats?.bought_seats;

  const { getAccessTokenSilently } = useAuth();

  const openSelfServePortal = useCallback(
    async (subscriptionId?: string | null) => {
      try {
        const forwardOptions = subscriptionId
          ? {
              sectionType: window?.Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
              params: {
                subscriptionId: subscriptionId,
              },
            }
          : undefined;

        let subscriptionUpdated = false;

        cbInstance.setPortalSession(async () => {
          const token = await getAccessTokenSilently();
          const response = await fetch(
            `${process.env.REACT_APP_HTTP_API_URL}/api/chargebee/self-serve-portal`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'client-referer': window?.location?.hostname,
              },
            },
          );
          const res = await response.text();
          return JSON.parse(res);
        });

        let cbPortal = cbInstance.createChargebeePortal();

        cbPortal.open(
          {
            close: async () => {
              if (subscriptionUpdated && refetchOwnerSubscription && setRefetchLoading) {
                setRefetchLoading(true);
                await refetchOwnerSubscription();
                setRefetchLoading(false);
              }
            },
            subscriptionChanged: () => {
              subscriptionUpdated = true;
            },
          },
          forwardOptions,
        );
      } catch (e) {}
    },
    [setRefetchLoading, getAccessTokenSilently, refetchOwnerSubscription, cbInstance],
  );

  useEffect(() => {
    if (info?.type || info?.renews_automaticly_on) {
      updateUserComPlanInfo({
        plan: info?.type,
        renewDate: info?.renews_automaticly_on,
      });
    }
  }, [info]);

  return (
    <>
      <CardSection
        title={translate('page.account.plan')}
        text={[translate(ownerInfo ? 'page.account.planOwnerDesc' : 'page.account.planDesc')]}
      >
        <div>
          <Typography className={classes.title}>
            {b2cInfo
              ? b2cInfo?.type
              : ownerInfo
              ? translate('page.account.planTitle', {
                  planName: info?.type,
                  companyName: companyName,
                })
              : translate('page.account.b2bPlanTitle', {
                  planName: info?.type,
                  companyName: companyName,
                })}
          </Typography>
          <div>
            <div className={classes.iconItem}>
              <CalendarIcon className={classes.iconItemIcon} color={colorsPalette.blue} />
              <Typography className={classes.text}>
                {translate('page.account.paid')} {translate(`page.account.${info?.pay_frequency}`)}
              </Typography>
            </div>
            <div className={classes.iconItem}>
              <RenewIcon className={classes.iconItemIcon} color={colorsPalette.blue} />
              <Typography className={classes.text}>
                {translate('page.account.renewsOn', { date: info?.renews_automaticly_on })}
              </Typography>
            </div>
            {!!b2cInfo?.remaining_trials?.left && (
              <div className={classes.iconItem}>
                <CalendarIcon className={classes.iconItemIcon} color={colorsPalette.blue} />
                <Typography className={classes.text}>
                  {translate('page.account.trialInfo', {
                    current: b2cInfo?.remaining_trials?.left,
                    total: b2cInfo?.remaining_trials?.total,
                  })}
                </Typography>
              </div>
            )}
            {!!ownerInfo?.remaining_trials?.left && (
              <div className={classes.iconItem}>
                <CalendarIcon className={classes.iconItemIcon} color={colorsPalette.blue} />
                <Typography className={classes.text}>
                  {translate('page.account.trialInfo', {
                    current: ownerInfo?.remaining_trials?.left,
                    total: ownerInfo?.remaining_trials?.total,
                  })}
                </Typography>
              </div>
            )}
          </div>

          {(ownerInfo || b2cInfo) && (
            <CustomButton btnClass={classes.manageBtn} onClick={() => openSelfServePortal()}>
              {translate('button.manageMySubscription')}
            </CustomButton>
          )}
        </div>
      </CardSection>
      {ownerInfo && (
        <CardSection
          title={translate('page.account.learnersTitle')}
          text={[translate('page.account.learnersDesc')]}
        >
          <div>
            <Typography className={classes.title}>{translate('page.account.seats')}</Typography>
            <Typography className={classes.text}>
              {translate('page.account.seatsInfo', {
                current: ownerInfo?.seats?.occupied_seats,
                total: ownerInfo?.seats?.bought_seats,
              })}
            </Typography>
            {!allPlacesOccupied ? (
              <Link to={MY_ACCOUNT_LINKS.MyTeams}>
                <IconButton
                  btnClass={classes.manageBtn}
                  icon={
                    <AddUserIcon className={classes.buttonIcon} color={colorsPalette.primary} />
                  }
                  label={translate('button.addLearners')}
                />
              </Link>
            ) : (
              <IconButton
                btnClass={classes.manageBtn}
                icon={<AddUserIcon className={classes.buttonIcon} color={colorsPalette.white} />}
                label={translate('button.addLearners')}
                disabled
              />
            )}
          </div>
        </CardSection>
      )}
    </>
  );
};

export default SubscriptionInfo;
